import React, { useEffect, useRef, useState } from 'react';
import { extractMessageContent } from '../../utils/messageHelpers';
import { getCSWStatus } from '../../utils/conversationHelpers';
import { getFileTypeFromFile, clearFileSelection } from '../../utils/fileHelpers';
import { Message } from '../../types/wa_messages';
import { authFetch } from '../../utils/authFetch';

interface MessageInputPanelProps {
  selectedConversationId: string | null;
  selectedConversation: any;
  newMessage: string;
  setNewMessage: (message: string) => void;
  sending: boolean;
  setSending: (sending: boolean) => void;
  sendError: string;
  setSendError: (error: string) => void;
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  filePreviewUrl: string | null;
  setFilePreviewUrl: (url: string | null) => void;
  uploadProgress: number;
  setUploadProgress: (progress: number) => void;
  uploadedMediaUrl: string | null;
  setUploadedMediaUrl: (url: string | null) => void;
  uploading: boolean;
  setUploading: (uploading: boolean) => void;
  uploadError: string;
  setUploadError: (error: string) => void;
  gcsUri: string | null;
  setGcsUri: (uri: string | null) => void;
  replyingTo: Message | null;
  setReplyingTo: (msg: Message | null) => void;
  waTemplates: any[];
  waTemplatesLoading: boolean;
  waTemplatesError: string;
  setSelectedTemplate: (template: any) => void;
  setTemplateModalOpen: (open: boolean) => void;
  setTemplateVars: (vars: { [key: string]: string }) => void;
  handleSendMessage: () => Promise<void>;
  sendMessageWithMedia: (mediaUrl: string | null, gcsUri?: string) => Promise<void>;
  accessToken: string | null;
  organizationId: string | null;
  setUserAndToken: (user: any, token: string) => void;
  messages?: Message[];
  onMessageSent?: () => void; // Callback to refresh messages after sending
  onConversationUpdate?: () => void; // Callback to refresh conversation data after updating
}

const MessageInputPanel: React.FC<MessageInputPanelProps> = ({
  selectedConversationId,
  selectedConversation,
  newMessage,
  setNewMessage,
  sending,
  setSending,
  sendError,
  setSendError,
  selectedFile,
  setSelectedFile,
  filePreviewUrl,
  setFilePreviewUrl,
  uploadProgress,
  setUploadProgress,
  uploadedMediaUrl,
  setUploadedMediaUrl,
  uploading,
  setUploading,
  uploadError,
  setUploadError,
  gcsUri,
  setGcsUri,
  replyingTo,
  setReplyingTo,
  waTemplates,
  waTemplatesLoading,
  waTemplatesError,
  setSelectedTemplate,
  setTemplateModalOpen,
  setTemplateVars,
  handleSendMessage,
  sendMessageWithMedia,
  accessToken,
  organizationId,
  setUserAndToken,
  messages,
  onMessageSent,
  onConversationUpdate,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailAttachmentInputRef = useRef<HTMLInputElement>(null);
  const emailBodyEditorRef = useRef<HTMLDivElement>(null);
  const [channel, setChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [templateLoading, setTemplateLoading] = useState<string | null>(null);
  const cswClosed = selectedConversation ? getCSWStatus(selectedConversation, messages).closed : false;
  
  // Email composition state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [showEmailOptions, setShowEmailOptions] = useState(false);
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [emailReplyMode, setEmailReplyMode] = useState<'reply' | 'new'>('reply'); // 'reply' to thread, 'new' for new thread
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [showThreadSelectionModal, setShowThreadSelectionModal] = useState(false);
  const [isComposeExpanded, setIsComposeExpanded] = useState(false);
  
  // Modal state for adding missing channel
  const [showAddChannelModal, setShowAddChannelModal] = useState(false);
  const [pendingChannel, setPendingChannel] = useState<'whatsapp' | 'email' | null>(null);
  const [addChannelPhoneNumber, setAddChannelPhoneNumber] = useState('');
  const [addChannelEmail, setAddChannelEmail] = useState('');
  const [addChannelCountryCode, setAddChannelCountryCode] = useState('+65');
  const [addChannelLoading, setAddChannelLoading] = useState(false);
  const [addChannelError, setAddChannelError] = useState('');

  // Get available email threads from conversation
  const getEmailThreads = (): Array<{ thread_id: string; subject_text: string }> => {
    return selectedConversation?.email_threads || [];
  };

  // Find latest email thread_id for replying (for backward compatibility)
  const getLatestEmailThreadId = (): string | null => {
    // First, check if latest_message from conversation is an email with a thread_id
    if (selectedConversation?.latest_message) {
      const latestMsg = selectedConversation.latest_message;
      if (latestMsg.channel === 'email' && latestMsg.gmail_thread_id) {
        return latestMsg.gmail_thread_id;
      }
    }
    
    // Fallback: check the paginated messages array
    if (!messages || messages.length === 0) return null;
    
    // Find the latest email message (inbound or outbound) with a thread_id
    const emailMessages = messages
      .filter(msg => msg.channel === 'email' && msg.gmail_thread_id)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    return emailMessages.length > 0 ? emailMessages[0].gmail_thread_id || null : null;
  };

  // Get subject for selected thread
  const getSelectedThreadSubject = (): string | null => {
    if (!selectedThreadId) return null;
    const threads = getEmailThreads();
    const thread = threads.find(t => t.thread_id === selectedThreadId);
    return thread?.subject_text || null;
  };

  // Reset thread selection when conversation changes
  useEffect(() => {
    setSelectedThreadId(null);
    setEmailSubject('');
    setShowThreadSelectionModal(false);
    setIsComposeExpanded(false); // Collapse compose section when conversation changes
  }, [selectedConversationId]);

  // Determine default channel based on pic_contact availability
  useEffect(() => {
    if (!selectedConversation?.pic_contact) {
      // No pic_contact, default to whatsapp
      setChannel('whatsapp');
      return;
    }

    const hasEmail = !!selectedConversation.pic_contact.email;
    const hasWhatsApp = !!selectedConversation.pic_contact.whatsapp;

    if (hasEmail && !hasWhatsApp) {
      setChannel('email');
    } else if (hasWhatsApp && !hasEmail) {
      setChannel('whatsapp');
    } else if (hasEmail && hasWhatsApp) {
      // Both exist, default to whatsapp (can be changed by user)
      setChannel('whatsapp');
    } else {
      // Neither exists, default to whatsapp
      setChannel('whatsapp');
    }
  }, [selectedConversationId, selectedConversation]);

  // Auto-set reply mode and thread selection based on available threads
  useEffect(() => {
    if (channel === 'email') {
      const threads = getEmailThreads();
      
      if (emailReplyMode === 'reply') {
        if (threads.length === 0) {
          // No threads available, switch to new thread mode
          setEmailReplyMode('new');
          setSelectedThreadId(null);
          setEmailSubject('');
        } else if (threads.length === 1) {
          // Only one thread, auto-select it
          if (selectedThreadId !== threads[0].thread_id) {
            setSelectedThreadId(threads[0].thread_id);
            setEmailSubject(threads[0].subject_text);
          }
        } else {
          // Multiple threads - show modal if no thread is selected yet
          if (!selectedThreadId && threads.length > 1) {
            setShowThreadSelectionModal(true);
          } else if (selectedThreadId) {
            // Update subject if thread is selected
            const subject = getSelectedThreadSubject();
            if (subject && emailSubject !== subject) {
              setEmailSubject(subject);
            }
          }
        }
      } else {
        // New thread mode - clear selected thread and subject if it was from a thread
        if (selectedThreadId) {
          setSelectedThreadId(null);
          const currentSubject = getSelectedThreadSubject();
          if (currentSubject && emailSubject === currentSubject) {
            setEmailSubject('');
          }
        }
      }
    } else {
      // Not email channel - clear thread selection
      setSelectedThreadId(null);
    }
  }, [channel, emailReplyMode, selectedConversation]);

  // Handle thread selection from modal
  const handleThreadSelect = (threadId: string) => {
    setSelectedThreadId(threadId);
    const threads = getEmailThreads();
    const thread = threads.find(t => t.thread_id === threadId);
    if (thread) {
      setEmailSubject(thread.subject_text);
    }
    setShowThreadSelectionModal(false);
  };

  // Sync HTML editor content when switching modes
  useEffect(() => {
    if (channel === 'email' && isHtmlMode && emailBodyEditorRef.current) {
      // Initialize HTML editor with plain text converted to HTML if switching to HTML mode
      if (!emailBodyEditorRef.current.innerHTML || emailBodyEditorRef.current.innerHTML === '<br>') {
        if (emailBody) {
          emailBodyEditorRef.current.innerHTML = emailBody.replace(/\n/g, '<br>');
        }
      } else if (emailBodyEditorRef.current.innerHTML !== emailBody && !emailBody.includes('<')) {
        // If emailBody is plain text but editor has HTML, update editor
        emailBodyEditorRef.current.innerHTML = emailBody.replace(/\n/g, '<br>');
      }
    }
  }, [channel, isHtmlMode]);

  // Handle file change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadError('');
    setUploadedMediaUrl(null);
    if (file) {
      if (getFileTypeFromFile(file) === 'image') {
        setFilePreviewUrl(URL.createObjectURL(file));
      } else {
        setFilePreviewUrl(null);
      }
    } else {
      setFilePreviewUrl(null);
    }
  };

  // Clear file selection
  const clearFile = () => {
    clearFileSelection(
      setSelectedFile,
      setFilePreviewUrl,
      setUploadedMediaUrl,
      setGcsUri,
      setUploadProgress,
      setUploading,
      setUploadError
    );
  };

  // Replace actual line breaks with literal \n before sending
  const sendMessageNormalized = async () => {
    if (sending) return;
    if (channel === 'email') {
      await handleSendEmail();
      return;
    }
    const normalized = (newMessage || '').replace(/\r?\n/g, '\\n');
    setNewMessage(normalized);
    await handleSendMessage();
  };

  // Handle email attachment selection
  const handleEmailAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setEmailAttachments(prev => [...prev, ...files]);
  };

  // Remove email attachment
  const removeEmailAttachment = (index: number) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Send email
  const handleSendEmail = async () => {
    if (!selectedConversationId || !emailSubject.trim() || (!emailBody.trim() && emailAttachments.length === 0)) {
      setSendError('Subject is required and either body or attachment must be provided.');
      return;
    }

    setSending(true);
    setSendError('');

    try {
      // Convert email body to HTML if in HTML mode, otherwise use plain text
      const bodyText = isHtmlMode ? undefined : emailBody;
      const bodyHtml = isHtmlMode ? emailBody : undefined;

      // Parse CC and BCC
      const ccRecipients = emailCc.split(',').map(email => email.trim()).filter(email => email).map(email => ({ email }));
      const bccRecipients = emailBcc.split(',').map(email => email.trim()).filter(email => email).map(email => ({ email }));

      // Get thread_id if replying to existing thread (use selectedThreadId if available, otherwise fallback)
      const threadId = emailReplyMode === 'reply' 
        ? (selectedThreadId || getLatestEmailThreadId())
        : null;

      const requestBody: any = {
        subject: emailSubject.trim(),
        body_text: bodyText,
        body_html: bodyHtml,
        ...(ccRecipients.length > 0 && { cc: ccRecipients }),
        ...(bccRecipients.length > 0 && { bcc: bccRecipients }),
        ...(threadId && { thread_id: threadId }),
      };

      // Process attachments: convert File objects to base64
      const attachments: Array<{ filename: string; content: string; content_type?: string }> = [];
      if (emailAttachments.length > 0) {
        try {
          for (const file of emailAttachments) {
            // Check file size (25MB limit)
            const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
            if (file.size > MAX_FILE_SIZE) {
              setSendError(`File "${file.name}" exceeds 25MB limit. Maximum file size is 25MB.`);
              setSending(false);
              return;
            }

            // Convert file to base64
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                // Remove data URL prefix (e.g., "data:image/png;base64,")
                const base64 = (reader.result as string).split(',')[1];
                resolve(base64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            // Determine content type
            const contentType = file.type || undefined;

            attachments.push({
              filename: file.name,
              content: base64Content,
              content_type: contentType
            });
          }

          // Check total size
          const totalSize = emailAttachments.reduce((sum, file) => sum + file.size, 0);
          const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB
          if (totalSize > MAX_TOTAL_SIZE) {
            setSendError(`Total attachment size (${(totalSize / 1024 / 1024).toFixed(2)}MB) exceeds 25MB limit.`);
            setSending(false);
            return;
          }

          // Add attachments to request body
          if (attachments.length > 0) {
            requestBody.attachments = attachments;
          }
        } catch (error) {
          console.error('Failed to process attachments:', error);
          setSendError('Failed to process attachments. Please try again.');
          setSending(false);
          return;
        }
      }

      const resp = await authFetch(
        (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') +
        `/api/v1/admin/conversations/${selectedConversationId}/send_email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        },
        accessToken,
        setUserAndToken
      );

      if (resp.ok) {
        // Clear email form
        setEmailSubject('');
        setEmailBody('');
        setEmailAttachments([]);
        setEmailCc('');
        setEmailBcc('');
        setIsHtmlMode(false);
        setReplyingTo(null);
        setSelectedThreadId(null);
        
        // Refresh messages - trigger parent component to refetch
        if (onMessageSent) {
          onMessageSent();
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        setSendError(err.detail || 'Failed to send email.');
      }
    } catch (e) {
      setSendError('Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  // Format text (bold, italic, etc.) - simple implementation
  const formatText = (command: string, value?: string) => {
    if (emailBodyEditorRef.current) {
      emailBodyEditorRef.current.focus();
      document.execCommand(command, false, value);
      // Update email body from contentEditable
      if (emailBodyEditorRef.current) {
        setEmailBody(emailBodyEditorRef.current.innerHTML);
      }
    }
  };

  // Check if conversation has a specific channel
  const hasChannel = (channelType: 'whatsapp' | 'email'): boolean => {
    if (!selectedConversation?.pic_contact) return false;
    return channelType === 'email' 
      ? !!selectedConversation.pic_contact.email 
      : !!selectedConversation.pic_contact.whatsapp;
  };

  // Handle channel change - check if channel exists, show modal if not
  const handleChannelChange = (newChannel: 'whatsapp' | 'email') => {
    if (hasChannel(newChannel)) {
      // Channel exists, just switch to it
      setChannel(newChannel);
    } else {
      // Channel doesn't exist, show modal to add it
      setPendingChannel(newChannel);
      setAddChannelError('');
      setAddChannelPhoneNumber('');
      setAddChannelEmail('');
      setAddChannelCountryCode('+65');
      setShowAddChannelModal(true);
    }
  };

  // Update conversation with new channel info
  const handleAddChannel = async () => {
    if (!selectedConversationId || !pendingChannel || !organizationId) {
      setAddChannelError('Missing required information');
      return;
    }

    setAddChannelLoading(true);
    setAddChannelError('');

    try {
      if (!accessToken) {
        setAddChannelError('Authentication required');
        return;
      }

      // Validate input based on channel
      if (pendingChannel === 'whatsapp') {
        if (!addChannelPhoneNumber.trim()) {
          setAddChannelError('Please enter a WhatsApp number');
          setAddChannelLoading(false);
          return;
        }
      } else if (pendingChannel === 'email') {
        if (!addChannelEmail.trim()) {
          setAddChannelError('Please enter an email address');
          setAddChannelLoading(false);
          return;
        }
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(addChannelEmail.trim())) {
          setAddChannelError('Please enter a valid email address');
          setAddChannelLoading(false);
          return;
        }
      }

      // Get existing pic_contact and merge new channel
      const existingPicContact = selectedConversation?.pic_contact || {};
      const updatedPicContact = {
        ...existingPicContact,
      };

      if (pendingChannel === 'whatsapp') {
        const picContact = `${addChannelCountryCode}${addChannelPhoneNumber}`;
        updatedPicContact.whatsapp = picContact;
      } else if (pendingChannel === 'email') {
        updatedPicContact.email = addChannelEmail.trim();
      }

      // Update the conversation
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organization_id: organizationId,
            pic_contact: updatedPicContact,
            pic_name: selectedConversation?.pic_name || '',
            channel: pendingChannel,
            xero_direct_contact_id: selectedConversation?.xero_direct_contact_id
          })
        },
        accessToken,
        setUserAndToken
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setAddChannelError(errorData.detail || 'Failed to update conversation');
        setAddChannelLoading(false);
        return;
      }

      // Success - close modal and switch to the new channel
      setShowAddChannelModal(false);
      setPendingChannel(null);
      setAddChannelPhoneNumber('');
      setAddChannelEmail('');
      setAddChannelCountryCode('+65');
      setChannel(pendingChannel);

      // Refresh conversation data
      if (onConversationUpdate) {
        onConversationUpdate();
      } else if (onMessageSent) {
        // Fallback to onMessageSent if onConversationUpdate not provided
        onMessageSent();
      }
    } catch (err: any) {
      setAddChannelError(err.message || 'Failed to update conversation');
    } finally {
      setAddChannelLoading(false);
    }
  };

  // Handle template click with data fetching
  const handleTemplateClick = async (template: any) => {
    if (!organizationId) {
      // Fallback to original behavior if no organization ID
      setSelectedTemplate(template);
      setTemplateModalOpen(true);
      setTemplateVars({});
      return;
    }

    setTemplateLoading(template.name);
    try {
      const resp = await authFetch(
        (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1/admin/get_template_data_by_name',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organization_id: organizationId,
            template: template,
            pic_name: selectedConversation?.pic_name || undefined
          })
        },
        accessToken,
        setUserAndToken
      );

      if (resp.ok) {
        const data = await resp.json();
        setSelectedTemplate(template);
        setTemplateModalOpen(true);
        setTemplateVars(data.template_data || {});
      } else {
        // Fallback to original behavior on error
        setSelectedTemplate(template);
        setTemplateModalOpen(true);
        setTemplateVars({});
      }
    } catch (error) {
      console.error('Error fetching template data:', error);
      // Fallback to original behavior on error
      setSelectedTemplate(template);
      setTemplateModalOpen(true);
      setTemplateVars({});
    } finally {
      setTemplateLoading(null);
    }
  };

  return (
    <div className="text-left">
      <>
      {/* File preview absolutely positioned inside this container */}
      {selectedFile && (
        <div className="absolute left-4 bottom-24 z-20 w-28 rounded-xl shadow-lg bg-white flex flex-col items-center p-3 border border-blue-200" style={{ pointerEvents: 'auto' }}>
          {/* Remove button in top right */}
          <button
            onClick={clearFile}
            className="absolute top-2 right-2 text-slate-400 hover:text-red-500 bg-white bg-opacity-80 rounded-full p-1"
            title="Remove file"
            disabled={uploading}
            style={{ zIndex: 2 }}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M6 6l8 8M6 14L14 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
          {/* Preview image or extension */}
          {filePreviewUrl ? (
            <img src={filePreviewUrl} alt="preview" className="w-24 h-24 object-cover rounded-lg border" />
          ) : (
            <div className="w-24 h-24 flex items-center justify-center bg-slate-100 rounded-lg border relative">
              <span className="text-lg font-bold text-slate-600 bg-white bg-opacity-80 px-4 py-2 rounded">
                {selectedFile.name.split('.').pop()?.toUpperCase()}
              </span>
            </div>
          )}
          {/* File name at bottom, left-aligned */}
          <div className="mt-2 w-full text-left text-xs text-slate-900" style={{ fontFamily: 'Menlo, monospace' }}>{selectedFile.name}</div>
          {/* Progress bar and error below preview */}
          {uploading && (
            <div className="w-full bg-slate-200 rounded h-2 mt-2">
              <div className="bg-blue-500 h-2 rounded" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          )}
          {uploadError && <div className="text-xs text-red-500 mt-1">{uploadError}</div>}
        </div>
      )}

      {/* Reply overlay */}
      {replyingTo && (
        <div className="absolute left-4 bottom-24 z-20 w-80 rounded-xl shadow-lg bg-white flex items-center p-3 border border-blue-200" style={{ minHeight: 56, pointerEvents: 'auto' }}>
          <div className="flex-1 min-w-0">
            {/* Quoted message content, handle all types */}
            {(() => {
              const extracted = extractMessageContent(replyingTo);
              if (extracted.type === 'image' && (extracted.previewUrl || extracted.imageUrl)) {
                return (
                  <div className="flex items-center gap-3">
                    <img src={extracted.previewUrl || extracted.imageUrl} alt="quoted-img" className="w-12 h-12 object-cover rounded border" />
                    <div className="flex flex-col min-w-0">
                      {extracted.text && <div className="text-xs text-slate-900">{extracted.text}</div>}
                      <div className="text-xs text-slate-400">Image</div>
                    </div>
                  </div>
                );
              } else if (extracted.type === 'document' && extracted.documentUrl) {
                return (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded border">
                      <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#fff" /><path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.828A2 2 0 0 0 18.414 8L14 3.586A2 2 0 0 0 12.586 3H7z" fill="#a3a3a3" /><text x="8" y="19" fontSize="8" fill="#a3a3a3" fontFamily="monospace">FILE</text></svg>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="text-xs text-slate-900">{extracted.documentName}</div>
                      {extracted.text && <div className="text-xs text-slate-900">{extracted.text}</div>}
                    </div>
                  </div>
                );
              } else if (extracted.type === 'text' && extracted.text) {
                return <div className="text-xs text-slate-900">{extracted.text}</div>;
              } else {
                return <div className="text-xs text-slate-400 italic">[No preview]</div>;
              }
            })()}
          </div>
          <button
            className="ml-4 text-slate-400 hover:text-red-500 bg-white bg-opacity-80 rounded-full p-1"
            title="Cancel reply"
            onClick={() => setReplyingTo(null)}
            style={{ zIndex: 2 }}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M6 6l8 8M6 14L14 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
        </div>
      )}

      {/* Input box */}
      {selectedConversationId && selectedConversation && (() => {
        const closed = cswClosed;
        return (
          <>
            <div className="border-t rounded-t-2xl border-slate-100 bg-slate-50 px-4 py-4 flex flex-col gap-4 flex-shrink-0">
              {/* Compose Msg section */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between w-full">
                  <span className="text-slate-400 text-sm font-medium">Compose Msg</span>
                  {/* Expand/Collapse button */}
                  <button
                    type="button"
                    onClick={() => setIsComposeExpanded(!isComposeExpanded)}
                    className="text-slate-400 hover:text-slate-600 transition-colors p-1"
                    title={isComposeExpanded ? 'Collapse' : 'Expand'}
                  >
                    <svg 
                      className={`w-4 h-4 transition-transform duration-300 ${!isComposeExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                
                {/* Channel selection and hint text */}
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="compose-channel" 
                        value="whatsapp" 
                        checked={channel === 'whatsapp'} 
                        onChange={() => handleChannelChange('whatsapp')} 
                      />
                      <span>WhatsApp</span>
                      {!hasChannel('whatsapp') && (
                        <span className="text-xs text-slate-400 ml-1">(not set)</span>
                      )}
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="compose-channel" 
                        value="email" 
                        checked={channel === 'email'} 
                        onChange={() => handleChannelChange('email')} 
                      />
                      <span>Email</span>
                      {!hasChannel('email') && (
                        <span className="text-xs text-slate-400 ml-1">(not set)</span>
                      )}
                    </label>
                  </div>
                  {closed && channel === 'whatsapp' && (
                    <span className="text-xs text-rose-600">WA-CSW is closed. Only template messages can be sent.</span>
                  )}
                </div>
              </div>

              {/* Collapsible content with animation */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isComposeExpanded 
                    ? 'max-h-[2000px] opacity-100' 
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div 
                  className={`transition-transform duration-300 ease-in-out ${
                    isComposeExpanded ? 'translate-y-0' : 'translate-y-4'
                  }`}
                  style={{
                    transformOrigin: 'top',
                  }}
                >
                  {/* Template section - only show when WhatsApp is selected */}
                  {channel === 'whatsapp' && (
                    <>
                      {/* Soft separation */}
                      <div className="border-t border-slate-200 mt-4"></div>
                      
                      <div className="flex flex-col items-start gap-2 mt-4">
                        <span className="text-slate-400 text-sm font-medium">Templates for WhatsApp Msg:</span>
                        {waTemplatesLoading ? (
                          <span className="text-xs text-slate-400">Loading templates...</span>
                        ) : waTemplatesError ? (
                          <span className="text-xs text-red-400">{waTemplatesError}</span>
                        ) : waTemplates.length === 0 ? (
                          <span className="text-xs text-slate-400">No templates found.</span>
                        ) : (
                          <div className="flex flex-wrap gap-2 max-w-full">
                            {waTemplates.map(t => (
                              <button
                                key={t.name}
                                className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-blue-100 border border-slate-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleTemplateClick(t)}
                                disabled={templateLoading === t.name}
                              >
                                {templateLoading === t.name ? 'Loading...' : t.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Hide compose UI when WhatsApp is selected and CSW is closed */}
            {!(channel === 'whatsapp' && closed) && (
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isComposeExpanded 
                    ? 'max-h-[2000px] opacity-100' 
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div 
                  className={`transition-transform duration-300 ease-in-out ${
                    isComposeExpanded ? 'translate-y-0' : 'translate-y-4'
                  }`}
                  style={{
                    transformOrigin: 'top',
                  }}
                >
                  {channel === 'email' ? (
                  /* Email Composition UI */
                  <div className="border-t rounded-b-2xl border-slate-100 bg-slate-50 px-4 py-4 flex flex-col gap-4 flex-shrink-0">
                    {/* Email Subject */}
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-slate-500 font-medium">Subject</label>
                        {emailReplyMode === 'reply' && selectedThreadId && getEmailThreads().length > 1 && (
                          <button
                            type="button"
                            onClick={() => setShowThreadSelectionModal(true)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            disabled={sending}
                          >
                            Change
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="Email subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        disabled={sending || (emailReplyMode === 'reply' && !!selectedThreadId)}
                        readOnly={emailReplyMode === 'reply' && !!selectedThreadId}
                      />
                    </div>

                    {/* Email Thread Reply Mode */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-slate-500 font-medium">Thread</label>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="email-reply-mode"
                            value="reply"
                            checked={emailReplyMode === 'reply'}
                            onChange={() => {
                              setEmailReplyMode('reply');
                              const threads = getEmailThreads();
                              if (threads.length > 1 && !selectedThreadId) {
                                setShowThreadSelectionModal(true);
                              }
                            }}
                            disabled={sending || getEmailThreads().length === 0}
                          />
                          <span>Reply to thread</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="email-reply-mode"
                            value="new"
                            checked={emailReplyMode === 'new'}
                            onChange={() => setEmailReplyMode('new')}
                            disabled={sending}
                          />
                          <span>New thread</span>
                        </label>
                      </div>
                      {emailReplyMode === 'reply' && getEmailThreads().length === 0 && (
                        <span className="text-xs text-amber-600">No existing email thread found. Starting a new thread.</span>
                      )}
                    </div>

                    {/* Email Body with Formatting Toolbar */}
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-slate-500 font-medium">Body</label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (isHtmlMode && emailBodyEditorRef.current) {
                                // Extract plain text when switching from HTML to plain text
                                const textContent = emailBodyEditorRef.current.innerText || emailBodyEditorRef.current.textContent || '';
                                setEmailBody(textContent);
                              }
                              setIsHtmlMode(!isHtmlMode);
                            }}
                            className={`text-xs px-2 py-1 rounded ${isHtmlMode ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}
                            title="Toggle HTML mode"
                          >
                            HTML
                          </button>
                          {isHtmlMode && (
                            <div className="flex items-center gap-1 border border-slate-200 rounded bg-white p-1">
                              <button
                                type="button"
                                onClick={() => formatText('bold')}
                                className="p-1 hover:bg-slate-100 rounded"
                                title="Bold"
                              >
                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6zM6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => formatText('italic')}
                                className="p-1 hover:bg-slate-100 rounded"
                                title="Italic"
                              >
                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M10 4h6M8 20h6M12 4l-2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => formatText('underline')}
                                className="p-1 hover:bg-slate-100 rounded"
                                title="Underline"
                              >
                                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M6 4v6a6 6 0 0 0 12 0V4M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {isHtmlMode ? (
                        <div
                          ref={emailBodyEditorRef}
                          contentEditable
                          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-100"
                          style={{ whiteSpace: 'pre-wrap' }}
                          onInput={(e) => {
                            const target = e.currentTarget;
                            setEmailBody(target.innerHTML);
                          }}
                          suppressContentEditableWarning
                        />
                      ) : (
                        <textarea
                          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-y min-h-[120px]"
                          placeholder="Email body (plain text)"
                          value={emailBody}
                          onChange={(e) => setEmailBody(e.target.value)}
                          disabled={sending}
                          rows={5}
                        />
                      )}
                    </div>

                    {/* Email Attachments */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-slate-500 font-medium">Attachments</label>
                        <button
                          type="button"
                          onClick={() => emailAttachmentInputRef.current?.click()}
                          className="text-xs text-blue-600 hover:text-blue-800"
                          disabled={sending}
                        >
                          + Add
                        </button>
                      </div>
                      <input
                        type="file"
                        ref={emailAttachmentInputRef}
                        style={{ display: 'none' }}
                        onChange={handleEmailAttachmentChange}
                        multiple
                        disabled={sending}
                      />
                      {emailAttachments.length > 0 && (
                        <div className="flex flex-col gap-1">
                          {emailAttachments.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white border border-slate-200 rounded px-2 py-1 text-xs">
                              <span className="truncate flex-1">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeEmailAttachment(idx)}
                                className="text-red-500 hover:text-red-700 ml-2"
                                disabled={sending}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Email Options (CC/BCC) */}
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => setShowEmailOptions(!showEmailOptions)}
                        className="text-xs text-slate-500 hover:text-slate-700 text-left"
                      >
                        {showEmailOptions ? '−' : '+'} CC / BCC
                      </button>
                      {showEmailOptions && (
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-slate-500 font-medium">CC</label>
                            <input
                              type="text"
                              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                              placeholder="cc@example.com (comma-separated)"
                              value={emailCc}
                              onChange={(e) => setEmailCc(e.target.value)}
                              disabled={sending}
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-slate-500 font-medium">BCC</label>
                            <input
                              type="text"
                              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                              placeholder="bcc@example.com (comma-separated)"
                              value={emailBcc}
                              onChange={(e) => setEmailBcc(e.target.value)}
                              disabled={sending}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Send Button */}
                    <div className="flex items-center justify-end gap-2">
                      <button
                        className="px-4 py-2 rounded-lg bg-[#4a67b7] flex items-center justify-center text-white hover:bg-blue-700 transition disabled:opacity-50"
                        onClick={handleSendEmail}
                        disabled={sending || !emailSubject.trim() || (!emailBody.trim() && emailAttachments.length === 0)}
                        title={sending ? 'Sending...' : 'Send email'}
                      >
                        {sending ? (
                          <>
                            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" className="animate-spin mr-2"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.2" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
                            Sending...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" className="mr-2"><path d="M3 12l18-7-7 18-2.5-7.5L3 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            Send Email
                          </>
                        )}
                      </button>
                    </div>
                    {sendError && <div className="text-red-500 text-xs">{sendError}</div>}
                  </div>
                ) : (
                  /* WhatsApp Composition UI */
                  <div className="border-t rounded-b-2xl border-slate-100 bg-slate-50 px-4 pb-4 flex items-center gap-3 flex-shrink-0">
              <button
                className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-blue-600"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
              >
                <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M16.5 8.5v-2A2.5 2.5 0 0 0 14 4h-4A2.5 2.5 0 0 0 7.5 6.5v2m9 0v2m0-2h-9m9 0h2.5A2.5 2.5 0 0 1 21 11v6.5A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5V11a2.5 2.5 0 0 1 2.5-2.5H7.5m9 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                disabled={uploading || sending}
              />
              <textarea
                className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-y min-h-[48px]"
                placeholder={"Type a message..."}
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey && !sending) {
                    e.preventDefault();
                    sendMessageNormalized();
                  }
                }}
                disabled={sending || !selectedConversationId}
                rows={2}
              />
              <button
                className="w-12 h-12 rounded-full bg-[#4a67b7] flex items-center justify-center text-white hover:bg-blue-700 transition disabled:opacity-50"
                onClick={sendMessageNormalized}
                disabled={sending || (!newMessage.trim() && !selectedFile) || !selectedConversationId || uploading || !channel}
                title={sending ? 'Sending...' : 'Send message'}
              >
                {sending ? (
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="animate-spin"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.2" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
                ) : (
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><path d="M3 12l18-7-7 18-2.5-7.5L3 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                )}
              </button>
                    {sendError && <div className="text-red-500 text-xs ml-2">{sendError}</div>}
                  </div>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}
      </>

      {/* Add Channel Modal */}
      {showAddChannelModal && pendingChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add {pendingChannel === 'email' ? 'Email' : 'WhatsApp'} to Conversation
              </h3>
              <button
                onClick={() => {
                  setShowAddChannelModal(false);
                  setPendingChannel(null);
                  setAddChannelError('');
                  setAddChannelPhoneNumber('');
                  setAddChannelEmail('');
                  setAddChannelCountryCode('+65');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  This conversation doesn't have a {pendingChannel === 'email' ? 'email address' : 'WhatsApp number'} yet. 
                  Please provide one to enable {pendingChannel === 'email' ? 'email' : 'WhatsApp'} messaging.
                </p>
              </div>

              {pendingChannel === 'whatsapp' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number
                  </label>
                  <div className="flex space-x-2">
                    <div className="relative">
                      <select
                        value={addChannelCountryCode}
                        onChange={(e) => setAddChannelCountryCode(e.target.value)}
                        className="px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                        disabled={addChannelLoading}
                      >
                        <option value="+1">+1 (US)</option>
                        <option value="+44">+44 (UK)</option>
                        <option value="+61">+61 (AU)</option>
                        <option value="+65">+65 (SG)</option>
                        <option value="+60">+60 (MY)</option>
                        <option value="+66">+66 (TH)</option>
                        <option value="+84">+84 (VN)</option>
                        <option value="+86">+86 (CN)</option>
                        <option value="+81">+81 (JP)</option>
                        <option value="+82">+82 (KR)</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <input
                      type="tel"
                      value={addChannelPhoneNumber}
                      onChange={(e) => setAddChannelPhoneNumber(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter phone number"
                      disabled={addChannelLoading}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={addChannelEmail}
                    onChange={(e) => setAddChannelEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="customer@example.com"
                    disabled={addChannelLoading}
                  />
                </div>
              )}

              {addChannelError && (
                <div className="text-red-600 text-sm">{addChannelError}</div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddChannelModal(false);
                    setPendingChannel(null);
                    setAddChannelError('');
                    setAddChannelPhoneNumber('');
                    setAddChannelEmail('');
                    setAddChannelCountryCode('+65');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  disabled={addChannelLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddChannel}
                  disabled={addChannelLoading}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    addChannelLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : pendingChannel === 'email'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {addChannelLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Adding...
                    </div>
                  ) : (
                    'Add'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Thread Selection Modal */}
      {showThreadSelectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black opacity-30" 
            onClick={() => setShowThreadSelectionModal(false)}
          ></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Email Thread</h3>
                <button
                  onClick={() => setShowThreadSelectionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-2">
                {getEmailThreads().map((thread) => (
                  <button
                    key={thread.thread_id}
                    onClick={() => handleThreadSelect(thread.thread_id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedThreadId === thread.thread_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900">{thread.subject_text}</div>
                    <div className="text-xs text-gray-500 mt-1">Thread ID: {thread.thread_id.substring(0, 20)}...</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageInputPanel; 